import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoom } from '../context/RoomContext';
import { useAuth } from '../context/AuthContext';
import './Controls.css';
import { Download, SaveOff } from 'lucide-react';

import { MonitorUp, Camera, CameraOff, Mic, MicOff,  Hand, SquareX } from 'lucide-react';

const Controls = ({ meetingId = null }) => {
  const navigate = useNavigate();
  const {
    isMuted,
    isVideoOff,
    isScreenSharing,
    isRecording,
    toggleMute,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    leaveRoom,
    endMeeting,
    startRecording,
    stopRecording,
    room,
    socketId,
    screenShareRequest,
    requestScreenShare,
    handRaised,
    toggleHand,
    recordingStartTime,
  } = useRoom();
  
  const { user } = useAuth();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isRecording) {
      setElapsed(0);
      return;
    }

    const id = setInterval(() => {
      if (recordingStartTime) {
        const diff = Math.floor((Date.now() - new Date(recordingStartTime).getTime()) / 1000);
        setElapsed(diff > 0 ? diff : 0);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [isRecording, recordingStartTime]);

  const formatTime = (seconds) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  // Check if current user is the host (use socketId to compare with room.hostId)
  const isHost = user && room && (room.hostId === socketId);

  const canRecord = !!user && !!room;

  // Handle share meeting link by email
  const handleShareLink = () => {
    const meetingUrl = `${window.location.origin}/join/${meetingId || room?.roomId}`;
    const subject = encodeURIComponent('Invitation à la réunion');
    const body = encodeURIComponent(`Bonjour,\n\nJe vous invite à rejoindre ma réunion.\n\nCliquez sur ce lien pour rejoindre : ${meetingUrl}\n\nÀ bientôt!`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  // Handle screen share button click
  const handleScreenShareClick = () => {
    if (isScreenSharing) {
      stopScreenShare();
    } else if (isHost) {
      // Host can always share screen
      startScreenShare();
    } else {
      // Participants need to request permission
      requestScreenShare();
    }
  };

  // Get screen share button text
  const getScreenShareText = () => {
    if (isScreenSharing) {
      return 'Désactivé le partage';
    } else if (isHost) {
      return 'Partagé écran';
    } else if (screenShareRequest === 'pending') {
      return 'Demande en cours...';
    } else if (screenShareRequest === 'approved') {
      return 'Partagé écran';
    } else {
      return 'Demander à partager';
    }
  };

  // Check if screen share button should be disabled
  const isScreenShareDisabled = !isHost && screenShareRequest === 'pending';

  return (
    <>
      <div className="controls">
        <button
          className={`control-btn ${isMuted ? 'active' : ''}`}
          onClick={toggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <MicOff /> : <Mic />}
          <span className="control-label">{isMuted ? 'Activé' : 'Désactivé'}</span>
        </button>

        <button
          className={`control-btn ${isVideoOff ? 'active' : ''}`}
          onClick={toggleVideo}
          title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
        >
          {isVideoOff ? <Camera /> : <CameraOff />}
          <span className="control-label">{isVideoOff ? 'Activé Video' : 'Désactivé Video'}</span>
        </button>

        <button
          className={`control-btn ${handRaised ? 'active hand-raised' : ''}`}
          onClick={toggleHand}
          title={handRaised ? 'Lower hand' : 'Raise hand'}
        >
          <Hand />
          <span className="control-label">{handRaised ? 'Baisser main' : 'Lever main'}</span>
        </button>

        <button
          className={`control-btn ${isScreenSharing ? 'active screen-share' : ''}`}
          onClick={handleScreenShareClick}
          disabled={isScreenShareDisabled}
          title={isScreenSharing ? 'Arrêter le partage' : 'Partager l\'écran'}
        >
          <MonitorUp />
          <span className="control-label">
            {getScreenShareText()}
          </span>
        </button>

        <button
          className="control-btn leave-btn"
          onClick={() => {
            console.log("ROOM =", room);
            console.log("meetingId =", meetingId);
        
            isRecording
              ? stopRecording(user.id, meetingId || room?.id_meeting || room?.meeting_code || room?.id)
              : startRecording(user.id, meetingId || room?.id_meeting || room?.meeting_code || room?.id);
        }}
          onClick={() => {
            leaveRoom();
            navigate('/');
          }}
          title="Quitter la réunion"
        >
          <SquareX />
          <span className="control-label">Quitter</span>
        </button>

        {/* Recording button - visible to all authenticated users in a room */}
        {canRecord && (
          <div className="recording-wrapper">
            <button
              className={`control-btn recording-btn ${isRecording ? 'recording' : ''}`}
              onClick={() => isRecording ? stopRecording(user.id, meetingId || room?.id_meeting || room?.meeting_code || room?.id) : startRecording(user.id, meetingId || room?.id_meeting || room?.meeting_code || room?.id)}
              title={isRecording ? 'Arrêter l\'enregistrement' : 'Démarrer l\'enregistrement'}
            >
              {isRecording ? <SaveOff /> :  <Download />}
              <span className="control-label">
                {isRecording ? 'Arrêter' : 'Enregistrer'}
              </span>
            </button>
            {isRecording && (
              <span className="recording-timer">{formatTime(elapsed)}</span>
            )}
          </div>
        )}

        </div>
    </>
  );
};

export default Controls;
